import React from 'react';
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  MenuList,
  MenuItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  Toolbar,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import {
  Theme,
  createStyles,
  makeStyles,
  styled,
  useTheme,
} from '@material-ui/core/styles';

import { PDFIcon } from '@bizapp-lp/home/atoms/Icon';
import { StyledButton } from '@bizapp-lp/home/molecules/StyledButton';

export interface HeaderContentProps {
  process?: HeaderMenuProps;
  communication?: HeaderMenuProps;
  knowledge?: HeaderMenuProps;
  disableButton?: boolean;
  onClick?: () => void;
}

export const HeaderContent: React.FC<HeaderContentProps> = ({
  process,
  communication,
  knowledge,
  disableButton,
  onClick,
}) => {
  const HeaderNav = styled('nav')(({ theme }) => ({
    '& > *': {
      marginLeft: theme.spacing(3),
    },
  }));

  const classes = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        width: '170px',
        height: '32px',
      },
    }),
  )();

  return (
    <>
      <HeaderNav>
        <HeaderMenu {...process} />
        <HeaderMenu {...communication} />
        <HeaderMenu {...knowledge} />
      </HeaderNav>
      <Box flexGrow={1} />
      {disableButton || (
        <StyledButton
          shallow
          label="お問い合わせ"
          onClick={onClick}
          className={classes.button}
        />
      )}
    </>
  );
};

export const HeaderContentDrawer: React.FC<HeaderContentProps> = ({
  process,
  communication,
  knowledge,
  disableButton,
  onClick,
}) => {
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const classes = makeStyles((theme: Theme) =>
    createStyles({
      menuButton: {
        margin: theme.spacing(0, -1.5, 0, 'auto'),
        color: '#7f7f7f',
      },
      dialog: {
        '& .MuiPaper-root': {
          position: 'absolute',
          top: 0,
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          borderRadius: 0,
        },
        '& .MuiDialog-paperScrollPaper': {
          maxHeight: 'calc(100% - 32px)',
        },
      },
      bgToolbar: { height: theme.spacing(9) },
      headerFirstMenu: {
        marginTop: theme.spacing(2),
      },
      dialogButton: {
        width: 'calc(100% - 32px)',
        maxWidth: '343px',
        height: '32px',
        minHeight: '32px',
        margin: theme.spacing(1.5, 'auto', 2.5),
      },
    }),
  )();

  return (
    <>
      <IconButton
        aria-label="open menu"
        edge="end"
        onClick={handleToggle}
        className={classes.menuButton}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        style={{ zIndex: theme.zIndex.appBar - 1 }}
        className={classes.dialog}
      >
        <>
          <Toolbar className={classes.bgToolbar} />
          <HeaderMenuList {...process} className={classes.headerFirstMenu} />
          <HeaderMenuList {...communication} />
          <HeaderMenuList {...knowledge} />
          {disableButton || (
            <StyledButton
              shallow
              label="お問い合わせ"
              className={classes.dialogButton}
              onClick={onClick}
            />
          )}
        </>
      </Dialog>
    </>
  );
};

export interface HeaderMenuItems {
  imgSrc?: string;
  imgAlt?: string;
  text?: React.ReactNode;
  enablePDFIcon?: boolean;
  onClick?: () => void;
}

export interface HeaderMenuProps {
  className?: string;
  title?: string;
  items?: HeaderMenuItems[];
  hoverColor?: string;
}

export const HeaderMenu: React.FC<HeaderMenuProps> = ({
  className,
  title,
  items,
  hoverColor,
}) => {
  const classes = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        display: 'inline-block',
      },
      menuTitle: {
        cursor: 'pointer',
        color: '#333333',
        textTransform: 'capitalize',
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 'normal',
        letterSpacing: 0,
        '&:hover': {
          color: hoverColor ?? '#333333',
        },
      },
      menuList: {
        padding: 0,
      },
      menuItem: {
        margin: theme.spacing(1.5, 0),
        padding: 0,
        '&:last-child': {
          padding: 0,
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:disabled': {
          opacity: 1,
        },
        '&.Mui-disabled': {
          opacity: 1,
        },
      },
      listItemAvatar: {
        minWidth: 'initial',
      },
      avatar: {
        width: '32px',
        height: '32px',
      },
      listItemText: {
        padding: theme.spacing(0, 1.5),
        color: '#333333',
        fontSize: '14px',
        lineHeight: '15px',
        fontWeight: 'normal',
        letterSpacing: 0,
        flexGrow: 0,
        '&:hover': {
          '& *': {
            color: hoverColor ?? '#333333',
          },
        },
      },
      listItemPDFIcon: {
        minWidth: 'initial',
        margin: 'auto 0',
      },
      popover: {
        pointerEvents: 'none',
      },
      paper: {
        pointerEvents: 'auto',
        padding: theme.spacing(1.5, 3),
        backgroundColor: '#ffffff',
        boxShadow: '0px 5px 10px #00000014',
        width: '272px',
        boxSizing: 'border-box',
      },
    }),
  )();

  const [openedPopover, setOpenedPopover] = React.useState(false);
  const popoverAnchor = React.useRef(null);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setOpenedPopover(true);
  };

  const handlePopoverClose = () => {
    setOpenedPopover(false);
  };

  return (
    <div className={`${classes.root} ${className}`}>
      <Typography
        className={classes.menuTitle}
        component="span"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        ref={popoverAnchor}
      >
        {title}
      </Typography>
      <Popover
        className={classes.popover}
        classes={{
          paper: classes.paper,
        }}
        open={openedPopover}
        anchorEl={popoverAnchor.current}
        anchorReference="anchorEl"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 68,
        }}
        PaperProps={{
          onMouseEnter: handlePopoverOpen,
          onMouseLeave: handlePopoverClose,
        }}
      >
        {items && (
          <MenuList className={classes.menuList}>
            {items.map((item, i) => (
              <MenuItem
                onClick={item.onClick}
                disabled={item.onClick === undefined}
                className={classes.menuItem}
                disableRipple
                key={i}
              >
                <ListItemAvatar className={classes.listItemAvatar}>
                  <Avatar
                    variant="square"
                    src={item.imgSrc}
                    alt={item.imgAlt}
                    className={classes.avatar}
                  />
                </ListItemAvatar>
                <ListItemText className={classes.listItemText}>
                  {item.text}
                </ListItemText>
                {item.enablePDFIcon && (
                  <ListItemIcon className={classes.listItemPDFIcon}>
                    <PDFIcon />
                  </ListItemIcon>
                )}
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Popover>
    </div>
  );
};

export const HeaderMenuList: React.FC<HeaderMenuProps> = ({
  className,
  title,
  items,
  hoverColor,
}) => {
  const classes = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        display: 'inline-block',
      },
      menuTitle: {
        padding: theme.spacing(2.5, 2, 2),
        color: '#636363',
        textTransform: 'capitalize',
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 'normal',
        letterSpacing: 0,
      },
      menuList: {
        padding: 0,
      },
      menuItem: {
        minHeight: 'initial',
        padding: theme.spacing(0, 2, 2),
        '&:last-child': {
          padding: theme.spacing(0, 2),
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:disabled': {
          opacity: 1,
        },
        '&.Mui-disabled': {
          opacity: 1,
        },
      },
      listItemAvatar: {
        minWidth: 'initial',
      },
      avatar: {
        width: '24px',
        height: '24px',
      },
      listItemText: {
        padding: theme.spacing(0, 1.5),
        color: '#333333',
        fontSize: '14px',
        lineHeight: '15px',
        fontWeight: 'normal',
        letterSpacing: 0,
        flexGrow: 0,
        '&:hover': {
          '& *': {
            color: hoverColor ?? '#333333',
          },
        },
      },
      listItemPDFIcon: {
        minWidth: 'initial',
        margin: 'auto 0',
      },
    }),
  )();
  return (
    <div className={`${classes.root} ${className}`}>
      <Typography className={classes.menuTitle} component="p">
        {title}
      </Typography>
      {items && (
        <MenuList className={classes.menuList}>
          {items.map((item, i) => (
            <MenuItem
              onClick={item.onClick}
              disabled={item.onClick === undefined}
              className={classes.menuItem}
              disableRipple
              key={i}
            >
              <ListItemAvatar className={classes.listItemAvatar}>
                <Avatar
                  variant="square"
                  src={item.imgSrc}
                  alt={item.imgAlt}
                  className={classes.avatar}
                />
              </ListItemAvatar>
              <ListItemText className={classes.listItemText}>
                {item.text}
              </ListItemText>
              {item.enablePDFIcon && (
                <ListItemIcon className={classes.listItemPDFIcon}>
                  <PDFIcon />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </MenuList>
      )}
    </div>
  );
};
